import {Box, Grid, TextField, Typography, Alert, Checkbox, FormControlLabel} from '@mui/material'
import React, {useEffect, useState} from 'react'
import DeckApi from '../logic/DeckApi'
import {useNavigate, useParams} from 'react-router-dom'
import ImageLogic from '../logic/ImageLogic'
import Button1 from '../components/Button1'
import CloseIcon from '@mui/icons-material/Close'
import {pink} from '@mui/material/colors'
import Button2 from '../components/Button2'
import LoadingBackdrop from '../components/LoadingBackdrop'
import {useAuth} from '../components/auth/AuthProvider'
import UserLogic from '../logic/UserLogic'
import {Deck} from '../model/models'
import ImageSelector from '../components/ImageSelector'
import TagsEditor from '../components/TagsEditor'

const api = new DeckApi()

export default function CreateAndEditDeck() {
  const [loading, setLoading] = useState<boolean>(true)
  const [isEditMode, setIsEditMode] = useState<boolean | null>(null)
  const [title, setTitle] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [imageFile, setImageFile] = useState<any>(null)
  const [isPublic, setIsPublic] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [titleError, setTitleError] = useState<string>('')
  const [imageError, setImageError] = useState<string>('')
  const [updateImage, setUpdateImage] = useState<boolean>(false)
  const [deckId, setDeckId] = useState<number>(-1)
  const [deck, setDeck] = useState<Deck | null>(null)
  const [hasContentCreatorRole, setHasContentCreatorRole] = useState<boolean>(false)
  const [initialImagePreviewUrl, setInitialImagePreviewUrl] = useState<string>('')
  const [tags, setTags] = useState<string[]>([])

  const {deckIdParam} = useParams()
  const navigate = useNavigate()
  const {user} = useAuth()

  useEffect(() => {
    const editMode = !!deckIdParam
    setIsEditMode(editMode)
    if (!editMode) {
      setLoading(false)
      return
    }

    const deckIdInt = parseInt(deckIdParam)

    setDeckId(deckIdInt)

    setHasContentCreatorRole(UserLogic.isContentCreator(user))

    const fetchData = async () => {
      const deckResponse = await api.getDeckWithoutCards(deckIdInt)
      if (deckResponse.isError()) {
        console.error('error getting deck', deckResponse.exception?.toJson())
        navigate('/error')
        return
      }
      const deck = deckResponse.data!
      setTitle(deck.title)
      setDescription(deck.description)
      if (deck.imageMeta) {
        setInitialImagePreviewUrl(ImageLogic.getImageUrl(deck.imageMeta))
      }
      setIsPublic(deck.isPublic)
      setTags(deck.tags)
      setDeck(deck)
    }

    fetchData().finally(() => setLoading(false))
  }, [])

  const saveDeck = async (e: any) => {
    e.preventDefault()

    if (!isEditMode) {
      const idResponse = await api.createDeck(title, description, imageFile, tags)
      if (idResponse.isError()) {
        const code = idResponse.statusCode
        const ex = idResponse.exception!
        if (code === 409) {
          const message = 'a deck with this name already exists'
          setErrorMessage(message)
          setTitleError('a deck with this name already exists')
        } else if (code === 400 && idResponse.exception && idResponse.exception.message.includes('deck limit')) {
          setErrorMessage(idResponse.exception.message.replaceAll('"', ''))
          return
        } else if (code === 400) {
          console.log(idResponse)
          setErrorMessage('one of the fields is invalid')
          if (ex.errors['Title']) {
            setTitleError(ex.errors['Title'])
          }
          if (ex.errors['ImageFile']) {
            setImageError(ex.errors['ImageFile'])
          }
        } else {
          setErrorMessage('unexpected server error - try again later')
        }
      }

      const newDeckId = idResponse.data!.id
      navigate(`/deck/${newDeckId}`)
      return
    } else {
      const res = await api.updateDeck(deckId, title, description, imageFile, updateImage, tags, isPublic)
      if (res.isError()) {
        console.error(res.exception?.toJson())
        setErrorMessage('unexpected error saving deck')
        return
      }
      navigate(`/deck/${deckId}`)
      return
    }
  }

  const imageChange = (imageFile: any) => {
    setImageFile(imageFile)
    setUpdateImage(true)
  }

  return (
    <Box>
      <LoadingBackdrop loading={loading}/>
      <Typography
        variant="h4"
        sx={{mt: 3}}>
        {isEditMode ? "Edit Deck" : "New Deck"}
      </Typography>
      <Box sx={{my: 1}}>
        <form noValidate autoComplete='off'>
          <TextField
            variant="filled"
            label="Deck Title"
            fullWidth
            margin="normal"
            value={title}
            onChange={e => {
              setTitle(e.target.value)
              setTitleError('')
              setErrorMessage('')
            }}
            error={!!titleError}
            helperText={titleError}
          >
          </TextField>
          <TextField
            sx={{mb: 2.5}}
            variant="filled"
            label="Description"
            margin="normal"
            fullWidth
            value={description}
            onChange={e => {
              setDescription(e.target.value)
            }}>
          </TextField>
          <Grid container sx={{mt: 1}}>
            <ImageSelector
              label="front image"
              change={imageFile => imageChange(imageFile)}
              refreshNumber={0}
              initialImagePreviewUrl={initialImagePreviewUrl}
            />
            {imageError && <Alert severity="error">{imageError}</Alert>}
          </Grid>

          <TagsEditor
            tags={tags}
            onChange={(updatedTags: string[]) => {
              setTags(updatedTags)
            }}/>

          {isEditMode && hasContentCreatorRole && <Box sx={{mt: 2}}>
            <FormControlLabel label="Public" control={<Checkbox
              checked={isPublic}
              onChange={e => setIsPublic(e.target.checked)}
            />}/>
          </Box>}
          <Grid container spacing={2} sx={{mt: 2}}>
            <Grid item xs={6} md={3} lg={2}>
              <Button1 onClick={saveDeck}>Save Deck</Button1>
            </Grid>
            <Grid item xs={6} md={3} lg={2}>
              <Button2
                onClick={_ => navigate(isEditMode ? `/deck/${deckId}` : '/my-decks')}
                startIcon={<CloseIcon sx={{color: pink[600]}}/>}>Cancel</Button2>
            </Grid>
          </Grid>
          {errorMessage && <Grid sx={{pt: '15px'}}>
            <Alert severity="error">{errorMessage}</Alert>
          </Grid>}
        </form>
      </Box>
    </Box>)
}
